import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { CASHBACK_RATE_MAX, CASHBACK_RATE_MIN, type CashbackRideTypeOverride } from "@safiri/shared";
import { cashbackApi } from "../../api/cashback";
import { rideTypesApi } from "../../api/rideTypes";
import { usePermission } from "../../auth/usePermission";
import { errorMessage } from "../../lib/axios";
import { formatDateTime } from "../../lib/format";
import { useI18n } from "../../i18n/context";

type Rate = number | "";
const toRate = (v: string | number): Rate => (v === "" ? "" : Number(v));

/**
 * Program switch, default rates and per-ride-type overrides (spec §7).
 * Every save creates a new settings version; rides keep the rates in force
 * when they completed, so changes only affect new rides.
 */
export function CashbackSettingsCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const canManage = usePermission("finance.manage");

  const settings = useQuery({ queryKey: ["cashback-settings"], queryFn: cashbackApi.settings });
  const history = useQuery({ queryKey: ["cashback-settings-history"], queryFn: cashbackApi.history });
  const rideTypes = useQuery({ queryKey: ["ride-types", "all"], queryFn: () => rideTypesApi.list() });

  const [enabled, setEnabled] = useState(false);
  const [passenger, setPassenger] = useState<Rate>("");
  const [driver, setDriver] = useState<Rate>("");
  const [overrides, setOverrides] = useState<CashbackRideTypeOverride[]>([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setEnabled(s.enabled);
    setPassenger(s.passengerPercentage);
    setDriver(s.driverPercentage);
    setOverrides(s.rideTypeOverrides);
  }, [settings.data]);

  const rideTypeOptions = useMemo(
    () =>
      (rideTypes.data?.items ?? []).map((r) => ({
        value: r._id,
        label: r.name?.en ?? r._id,
      })),
    [rideTypes.data],
  );
  const rideTypeName = (id: string) => rideTypeOptions.find((o) => o.value === id)?.label ?? id;

  const save = useMutation({
    mutationFn: () =>
      cashbackApi.updateSettings({
        enabled,
        passengerPercentage: Number(passenger),
        driverPercentage: Number(driver),
        rideTypeOverrides: overrides,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: t("cb.settingsSaved") });
      setNote("");
      qc.invalidateQueries({ queryKey: ["cashback-settings"] });
      qc.invalidateQueries({ queryKey: ["cashback-settings-history"] });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const valid =
    passenger !== "" &&
    driver !== "" &&
    overrides.every((o) => o.rideTypeId) &&
    new Set(overrides.map((o) => o.rideTypeId)).size === overrides.length;

  const rateInput = (value: Rate, onChange: (v: Rate) => void, label?: string, placeholder?: string) => (
    <NumberInput
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={(v) => onChange(toRate(v))}
      min={CASHBACK_RATE_MIN}
      max={CASHBACK_RATE_MAX}
      decimalScale={2}
      suffix="%"
      disabled={!canManage}
      w={label ? 160 : 110}
      size={label ? "sm" : "xs"}
    />
  );

  return (
    <Stack>
      <Card padding="lg">
        <Group justify="space-between" mb="xs">
          <Text fw={650}>{t("cb.settings")}</Text>
          {settings.data?.updatedAt && (
            <Text size="xs" c="dimmed">
              {t("cb.lastChanged", {
                at: formatDateTime(settings.data.updatedAt),
                by: settings.data.updatedBy ?? "—",
              })}
            </Text>
          )}
        </Group>
        {settings.isLoading ? (
          <Skeleton h={220} />
        ) : (
          <Stack>
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
              disabled={!canManage}
              label={enabled ? t("cb.programOn") : t("cb.programOff")}
              description={t("cb.switchHelp")}
            />
            <Group>
              {rateInput(passenger, setPassenger, t("cb.passengerRate"))}
              {rateInput(driver, setDriver, t("cb.driverRate"))}
            </Group>

            <div>
              <Text size="sm" fw={600}>{t("cb.overrides")}</Text>
              <Text size="xs" c="dimmed" mb="xs">{t("cb.overridesHelp")}</Text>
              {overrides.length > 0 && (
                <Table fz="sm" mb="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("cb.rideType")}</Table.Th>
                      <Table.Th>{t("cb.passenger")}</Table.Th>
                      <Table.Th>{t("cb.driver")}</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {overrides.map((o, i) => {
                      const set = (patch: Partial<CashbackRideTypeOverride>) =>
                        setOverrides((list) => list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                      return (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Select
                              size="xs"
                              data={rideTypeOptions.filter(
                                (opt) => opt.value === o.rideTypeId || !overrides.some((x) => x.rideTypeId === opt.value),
                              )}
                              value={o.rideTypeId || null}
                              onChange={(v) => set({ rideTypeId: v ?? "" })}
                              disabled={!canManage}
                              placeholder={t("cb.pickRideType")}
                              searchable
                            />
                          </Table.Td>
                          <Table.Td>
                            {rateInput(o.passengerPercentage ?? "", (v) => set({ passengerPercentage: v === "" ? null : v }), undefined, t("cb.useDefault"))}
                          </Table.Td>
                          <Table.Td>
                            {rateInput(o.driverPercentage ?? "", (v) => set({ driverPercentage: v === "" ? null : v }), undefined, t("cb.useDefault"))}
                          </Table.Td>
                          <Table.Td>
                            {canManage && (
                              <ActionIcon variant="subtle" color="red" aria-label={t("cb.removeOverride")} onClick={() => setOverrides((list) => list.filter((_, j) => j !== i))}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
              {canManage && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setOverrides((list) => [...list, { rideTypeId: "", passengerPercentage: null, driverPercentage: null }])}
                >
                  {t("cb.addOverride")}
                </Button>
              )}
            </div>

            {canManage ? (
              <Group align="flex-end">
                <TextInput
                  label={t("cb.changeNote")}
                  placeholder={t("cb.changeNotePlaceholder")}
                  value={note}
                  onChange={(e) => setNote(e.currentTarget.value)}
                  maxLength={300}
                  style={{ flex: 1, minWidth: 220 }}
                />
                <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
                  {t("cb.save")}
                </Button>
              </Group>
            ) : (
              <Alert color="gray">{t("cb.readOnly")}</Alert>
            )}
          </Stack>
        )}
      </Card>

      <Card padding="lg">
        <Text fw={650} mb="md">{t("cb.history")}</Text>
        {history.isLoading ? (
          <Skeleton h={120} />
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table fz="sm" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("cb.when")}</Table.Th>
                  <Table.Th>{t("cb.by")}</Table.Th>
                  <Table.Th>{t("cb.program")}</Table.Th>
                  <Table.Th>{t("cb.rates")}</Table.Th>
                  <Table.Th>{t("cb.overrides")}</Table.Th>
                  <Table.Th>{t("cb.note")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(history.data ?? []).map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>{formatDateTime(v.effectiveFrom)}</Table.Td>
                    <Table.Td>{v.changedBy ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge color={v.enabled ? "green" : "gray"} variant="light">
                        {v.enabled ? t("cb.on") : t("cb.off")}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{v.passengerPercentage}% / {v.driverPercentage}%</Table.Td>
                    <Table.Td>
                      {v.rideTypeOverrides.length === 0
                        ? "—"
                        : v.rideTypeOverrides
                            .map((o) => `${rideTypeName(o.rideTypeId)} ${o.passengerPercentage ?? "·"}% / ${o.driverPercentage ?? "·"}%`)
                            .join(", ")}
                    </Table.Td>
                    <Table.Td>{v.note ?? ""}</Table.Td>
                  </Table.Tr>
                ))}
                {(history.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6} c="dimmed" ta="center">{t("cb.noHistory")}</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  );
}
